import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import PlateField from '../components/common/PlateField'
import { useAuth } from '../hooks/useAuth'
import { timesheetApi } from '../services/api'
import Button from '../components/common/Button'
import Input from '../components/common/Input'
import Select from '../components/common/Select'
import TextArea from '../components/common/TextArea'
import Modal from '../components/common/Modal'
import SignaturePad from '../components/signature/SignaturePad'
import toast from 'react-hot-toast'

const TIMESHEET_TYPES = [
    { value: '0', label: 'Timesheet' },
    { value: '3', label: 'Shop Work' },
    { value: '2', label: 'Packing Slip' },
    { value: '4', label: 'Pickup Slip' },
    { value: '5', label: 'Vendor Slip' },
]

const JOB_COMPLETE_OPTIONS = [
    { value: 'Yes', label: 'Yes' },
    { value: 'No', label: 'No' },
]

const VENDOR_SLIP_TYPE_OPTIONS = [
    { value: 'Drop Off for Repair', label: 'Drop Off Parts for Repair' },
    { value: 'Pick Up from Repair', label: 'Pick Up Parts from Repair' },
]

const MATERIAL_USED_OPTIONS = [
    { value: 'Yes', label: 'Yes' },
    { value: 'No', label: 'No' },
]

// Generate 15-minute increment times
const TIME_OPTIONS = []
for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
        const hour = h.toString().padStart(2, '0')
        const min = m.toString().padStart(2, '0')
        const value = `${hour}:${min}`
        const label = `${hour}:${min}`
        TIME_OPTIONS.push({ value, label })
    }
}

function getNearestTimeValue() {
    const now = new Date()
    const h = now.getHours()
    const m = Math.round(now.getMinutes() / 15) * 15
    const adjustedH = m === 60 ? h + 1 : h
    const adjustedM = m === 60 ? 0 : m
    return `${(adjustedH % 24).toString().padStart(2, '0')}:${adjustedM.toString().padStart(2, '0')}`
}

export default function TimesheetEntry() {
    const { uuid } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const { user, registerIdleSaver } = useAuth()
    const isEditing = !!uuid

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [savingSlipItem, setSavingSlipItem] = useState(false)
    const [savingPart, setSavingPart] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [returningToDraft, setReturningToDraft] = useState(false)
    const [showSignature, setShowSignature] = useState(false)
    const [formErrors, setFormErrors] = useState({})

    // Slip items state
    const [packingSlipItems, setPackingSlipItems] = useState([])
    const [pickupSlipItems, setPickupSlipItems] = useState([])
    const [vendorSlipItems, setVendorSlipItems] = useState([])
    const [pendingSlipItems, setPendingSlipItems] = useState([])
    const [showSlipItemModal, setShowSlipItemModal] = useState(false)
    const [slipItemType, setSlipItemType] = useState('packing')
    const [editingSlipItem, setEditingSlipItem] = useState(null)
    const [slipItemForm, setSlipItemForm] = useState({
        make: '',
        model: '',
        serialNumber: '',
        quantity: '',
        notes: ''
    })

    // Parts state
    const [parts, setParts] = useState([])
    const [pendingParts, setPendingParts] = useState([])
    const [showPartModal, setShowPartModal] = useState(false)
    const [editingPart, setEditingPart] = useState(null)
    const [partForm, setPartForm] = useState({
        part: '',
        description: '',
        quantity: '',
        listId: ''
    })

    // Get pre-filled data from navigation state
    const prefillData = location.state || {}

    // Form state with pre-filled values
    const [form, setForm] = useState({
        tsType: '0',
        referenceNo: '',
        workOrderNumber: prefillData.workOrderNumber || '',
        employeeName: user?.fullName || user?.userName || '',
        plateNumber: '',
        customer: prefillData.customer || '',
        contact: prefillData.contact || '',
        jobSite: prefillData.jobSite || '',
        month: new Date().toISOString().split('T')[0],
        siteStart: '',
        siteFinish: '',
        odomStart: '',
        odomFinish: '',
        machineNumber: '',
        make: '',
        model: '',
        serialNo: '',
        customerRequirement: prefillData.customerRequirement || '',
        workPerformed: '',
        signature: null,
        vendorName: '',
        vendorSlipType: '',
        status: '0',
    })

    // Read-only logic based on status
    const isDraft = form.status === '0' || form.status === 0
    const isSubmitted = form.status === '1' || form.status === 1
    const isExported = form.status === '2' || form.status === 2
    const isReadOnly = isSubmitted || isExported

    // Keep a ref of current form state so the idle saver always reads fresh values
    // without forcing a register/unregister cycle on every keystroke.
    const formRef = useRef(form)
    useEffect(() => { formRef.current = form }, [form])

    // Register a callback so that when the 2-hour idle timer fires, anything the
    // tech has typed gets persisted as a draft before they're logged out.
    useEffect(() => {
        if (!registerIdleSaver) return

        const idleSave = async () => {
            const currentForm = formRef.current
            if (isReadOnly) return
            if (!currentForm.customer) return // nothing meaningful to save yet

            try {
                if (uuid) {
                    await timesheetApi.update(uuid, currentForm)
                } else {
                    await timesheetApi.create(currentForm)
                }
            } catch (err) {
                console.error('Idle save failed:', err)
            }
        }

        return registerIdleSaver(idleSave)
    }, [uuid, isReadOnly, registerIdleSaver])

    // Load existing timesheet if editing
    useEffect(() => {
        if (isEditing) {
            loadTimesheet()
        }
    }, [uuid])

    // Load items when timesheet type changes or when editing
    useEffect(() => {
        if (isEditing && uuid) {
            if (form.tsType === '2') {
                loadPackingSlipItems()
            } else if (form.tsType === '4') {
                loadPickupSlipItems()
            } else if (form.tsType === '5') {
                loadVendorSlipItems()
            } else if (form.tsType === '0' || form.tsType === '3') {
                loadParts()
            }
        }
    }, [form.tsType, uuid])

    // Auto-open item/part modal if navigated here after auto-save
    useEffect(() => {
        if (isEditing && !loading && location.state?.openItemModal) {
            const type = location.state.openItemModal
            setSlipItemType(type)
            setEditingSlipItem(null)
            setSlipItemForm({ make: '', model: '', serialNumber: '', quantity: '', notes: '' })
            setShowSlipItemModal(true)
            navigate(location.pathname, { replace: true, state: { ...location.state, openItemModal: undefined, openPartModal: undefined } })
        } else if (isEditing && !loading && location.state?.openPartModal) {
            openPartModal()
            navigate(location.pathname, { replace: true, state: { ...location.state, openPartModal: undefined } })
        }
    }, [isEditing, loading])

    async function loadTimesheet() {
        setLoading(true)
        try {
            const response = await timesheetApi.getById(uuid)
            if (response.success) {
                setForm(response.timesheet)
            } else {
                toast.error('Failed to load timesheet')
                navigate('/timesheets')
            }
        } catch (error) {
            toast.error('Error loading timesheet')
            navigate('/timesheets')
        }
        setLoading(false)
    }

    async function loadPackingSlipItems() {
        try {
            const response = await timesheetApi.getPackingSlipItems(uuid)
            if (response.success) {
                setPackingSlipItems(response.items || [])
            }
        } catch (error) {
            console.error('Error loading packing slip items:', error)
        }
    }

    async function loadPickupSlipItems() {
        try {
            const response = await timesheetApi.getPickupSlipItems(uuid)
            if (response.success) {
                setPickupSlipItems(response.items || [])
            }
        } catch (error) {
            console.error('Error loading pickup slip items:', error)
        }
    }

    async function loadVendorSlipItems() {
        try {
            const response = await timesheetApi.getVendorSlipItems(uuid)
            if (response.success) {
                setVendorSlipItems(response.items || [])
            }
        } catch (error) {
            console.error('Error loading vendor slip items:', error)
        }
    }

    async function loadParts() {
        try {
            const response = await timesheetApi.getParts(uuid)
            if (response.success) {
                setParts(response.parts || [])
            }
        } catch (error) {
            console.error('Error loading parts:', error)
        }
    }

    function handleChange(field, value) {
        if (isReadOnly) return
        setForm(prev => ({ ...prev, [field]: value }))
        if (formErrors[field]) {
            setFormErrors(prev => {
                const next = { ...prev }
                delete next[field]
                return next
            })
        }
    }

    function handleTimeFocus(field) {
        if (isReadOnly) return
        if (!form[field]) {
            handleChange(field, getNearestTimeValue())
        }
    }

    // ==================== VALIDATION ====================

    function validateForm() {
        const errors = {}

        if (!form.employeeName?.trim()) errors.employeeName = 'Required'
        if (showPlateNumber && !form.plateNumber?.trim()) errors.plateNumber = 'Required'
        if (!form.customer?.trim()) errors.customer = 'Required'
        if (!form.workOrderNumber?.trim()) errors.workOrderNumber = 'Required'
        if (!form.month) errors.month = 'Required'
        if (!form.jobComplete) errors.jobComplete = 'Required'

        if (showJobSite && !form.jobSite?.trim()) errors.jobSite = 'Required'

        if (showVendorFields) {
            if (!form.vendorName?.trim()) errors.vendorName = 'Required'
            if (!form.vendorSlipType) errors.vendorSlipType = 'Required'
        }

        if (showSiteStartFinish) {
            if (!form.siteStart) errors.siteStart = 'Required'
            if (!form.siteFinish) errors.siteFinish = 'Required'
        }

        if (showOdomFields) {
            if (form.odomStart === '' || form.odomStart === null || form.odomStart === undefined) {
                errors.odomStart = 'Required'
            }
            if (form.odomFinish === '' || form.odomFinish === null || form.odomFinish === undefined) {
                errors.odomFinish = 'Required'
            }
            const start = parseInt(form.odomStart)
            const finish = parseInt(form.odomFinish)
            if (!isNaN(start) && !isNaN(finish) && start > finish) {
                errors.odomStart = 'Must be ≤ Odometer Finish'
            }
        }

        if (showMachineDetails) {
            if (!form.machineNumber?.trim()) errors.machineNumber = 'Required'
            if (!form.make?.trim()) errors.make = 'Required'
            if (!form.model?.trim()) errors.model = 'Required'
            if (!form.serialNo?.trim()) errors.serialNo = 'Required'
        }

        if (showCustomerRequirement && !form.customerRequirement?.trim()) errors.customerRequirement = 'Required'
        if (showWorkPerformed && !form.workPerformed?.trim()) errors.workPerformed = 'Required'
        if (showMaterialUsed && !form.materialUsed) errors.materialUsed = 'Required'

        return errors
    }

    // ==================== SAVE / SUBMIT ====================

    async function handleSave(navigateAfter = true) {
        if (isReadOnly) return
        if (!form.customer) {
            toast.error('Customer is required')
            return
        }
        setSaving(true)
        try {
            let response
            if (isEditing) {
                response = await timesheetApi.update(uuid, form)
            } else {
                response = await timesheetApi.create(form)
            }
            if (response.success) {
                const newUuid = response.uuid || uuid

                // Save any pending parts
                if (newUuid && pendingParts.length > 0) {
                    for (const p of pendingParts) {
                        try {
                            await timesheetApi.addPart(newUuid, {
                                partName: p.part,
                                description: p.description,
                                quantity: p.quantity,
                                listId: p.listId
                            })
                        } catch (err) {
                            console.error('Error saving pending part:', err)
                        }
                    }
                    setPendingParts([])
                }

                // Save any pending slip items
                if (newUuid && pendingSlipItems.length > 0) {
                    for (const item of pendingSlipItems) {
                        try {
                            const itemData = { make: item.make, model: item.model, serialNumber: item.serialNumber, quantity: item.quantity, notes: item.notes }
                            if (item.type === 'packing') {
                                await timesheetApi.addPackingSlipItem(newUuid, itemData)
                            } else if (item.type === 'vendor') {
                                await timesheetApi.addVendorSlipItem(newUuid, itemData)
                            } else {
                                await timesheetApi.addPickupSlipItem(newUuid, itemData)
                            }
                        } catch (err) {
                            console.error('Error saving pending slip item:', err)
                        }
                    }
                    setPendingSlipItems([])
                }

                // Save pending signature if present and new timesheet
                if (!isEditing && newUuid && form.signature) {
                    try {
                        await timesheetApi.saveSignature(newUuid, form.signature)
                    } catch (err) {
                        console.error('Error saving signature:', err)
                    }
                }

                toast.success(isEditing ? 'Timesheet updated' : 'Timesheet saved')
                if (!navigateAfter) {
                    if (!isEditing && response.uuid) {
                        navigate(`/timesheet/${response.uuid}`, { replace: true })
                    }
                } else {
                    if (location.state?.from === 'manage') {
                        navigate('/manage-timesheets')
                    } else {
                        navigate('/timesheets')
                    }
                }
            } else {
                toast.error(response.message || 'Failed to save')
            }
        } catch (error) {
            toast.error('Error saving timesheet')
        }
        setSaving(false)
    }

    async function handleSubmit() {
        if (!uuid) {
            toast.error('Please save the timesheet first')
            return
        }

        const errors = validateForm()
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors)
            toast.error(`Please fill in all required fields (${Object.keys(errors).length} missing)`)
            const firstErrorKey = Object.keys(errors)[0]
            const el = document.querySelector(`[data-field="${firstErrorKey}"]`)
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            return
        }

        setFormErrors({})
        setSubmitting(true)
        try {
            const saveResponse = await timesheetApi.update(uuid, form)
            if (!saveResponse.success) {
                toast.error(saveResponse.message || 'Failed to save before submitting')
                setSubmitting(false)
                return
            }

            const response = await timesheetApi.submit(uuid)
            if (response.success) {
                toast.success('Timesheet submitted!')
                if (location.state?.from === 'manage') {
                    navigate('/manage-timesheets')
                } else {
                    navigate('/timesheets')
                }
            } else if (response.qboError) {
                // QBO connection issue — leave the form in draft state, no navigation.
                // The tech will need to contact the office and try again later.
                toast.error(response.message, { duration: 8000 })
            } else {
                toast.error(response.message || 'Failed to submit')
            }
        } catch (error) {
            toast.error('Error submitting timesheet')
        }
        setSubmitting(false)
    }

    async function handleReturnToDraft() {
        if (!confirm('Return this timesheet to draft? It will need to be resubmitted after editing.')) return

        setReturningToDraft(true)
        try {
            const response = await timesheetApi.returnToDraft(uuid)
            if (response.success) {
                toast.success('Timesheet returned to draft')
                navigate('/timesheets')
            } else if (response.qboError) {
                // QBO couldn't delete the TimeActivity — leave the timesheet as submitted
                // so we don't end up with orphan payroll hours in QBO.
                toast.error(response.message, { duration: 8000 })
            } else {
                toast.error(response.message || 'Failed to return to draft')
            }
        } catch (error) {
            toast.error('Error returning to draft')
        }
        setReturningToDraft(false)
    }

    async function handleDelete() {
        if (!confirm('Are you sure you want to delete this timesheet?')) return

        setDeleting(true)
        try {
            const response = await timesheetApi.delete(uuid)
            if (response.success) {
                toast.success('Timesheet deleted')
                if (location.state?.from === 'manage') {
                    navigate('/manage-timesheets')
                } else {
                    navigate('/timesheets')
                }
            } else {
                toast.error(response.message || 'Failed to delete')
            }
        } catch (error) {
            toast.error('Error deleting timesheet')
        }
        setDeleting(false)
    }

    async function handleSignatureSave(signatureData) {
        if (isReadOnly) return
        setForm(prev => ({ ...prev, signature: signatureData }))
        setShowSignature(false)

        if (uuid) {
            try {
                const response = await timesheetApi.saveSignature(uuid, signatureData)
                if (response.success) {
                    toast.success('Signature saved')
                } else {
                    toast.error('Failed to save signature')
                }
            } catch (error) {
                toast.error('Error saving signature')
            }
        } else {
            toast.success('Signature captured - save timesheet to persist')
        }
    }

    function handleBack() {
        if (location.state?.from === 'manage') {
            navigate('/manage-timesheets')
        } else if (!isEditing) {
            navigate('/')
        } else {
            navigate('/timesheets')
        }
    }

    // ==================== SLIP ITEM FUNCTIONS ====================

    async function openSlipItemModal(type, item = null) {
        if (isReadOnly) return

        setSlipItemType(type)
        setEditingSlipItem(item)
        if (item) {
            setSlipItemForm({
                make: item.make || '',
                model: item.model || '',
                serialNumber: item.serialNumber || '',
                quantity: item.quantity || '',
                notes: item.notes || ''
            })
        } else {
            setSlipItemForm({ make: '', model: '', serialNumber: '', quantity: '', notes: '' })
        }
        setShowSlipItemModal(true)
    }

    async function handleSaveSlipItem() {
        if (!slipItemForm.quantity) {
            toast.error('Quantity is required')
            return
        }

        if (!uuid) {
            setPendingSlipItems(prev => [...prev, {
                id: Date.now(),
                type: slipItemType,
                make: slipItemForm.make,
                model: slipItemForm.model,
                serialNumber: slipItemForm.serialNumber,
                quantity: slipItemForm.quantity,
                notes: slipItemForm.notes,
                pending: true
            }])
            toast.success('Item added — will be saved with timesheet')
            setShowSlipItemModal(false)
            return
        }

        setSavingSlipItem(true)
        try {
            let response
            if (editingSlipItem) {
                if (slipItemType === 'packing') {
                    response = await timesheetApi.updatePackingSlipItem(editingSlipItem.uuid, slipItemForm)
                } else if (slipItemType === 'vendor') {
                    response = await timesheetApi.updateVendorSlipItem(editingSlipItem.uuid, slipItemForm)
                } else {
                    response = await timesheetApi.updatePickupSlipItem(editingSlipItem.uuid, slipItemForm)
                }
            } else {
                if (slipItemType === 'packing') {
                    response = await timesheetApi.addPackingSlipItem(uuid, slipItemForm)
                } else if (slipItemType === 'vendor') {
                    response = await timesheetApi.addVendorSlipItem(uuid, slipItemForm)
                } else {
                    response = await timesheetApi.addPickupSlipItem(uuid, slipItemForm)
                }
            }
            if (response.success) {
                toast.success(editingSlipItem ? 'Item updated' : 'Item added')
                setShowSlipItemModal(false)
                if (slipItemType === 'packing') {
                    loadPackingSlipItems()
                } else if (slipItemType === 'vendor') {
                    loadVendorSlipItems()
                } else {
                    loadPickupSlipItems()
                }
            } else {
                toast.error(response.message || 'Failed to save item')
            }
        } catch (error) {
            toast.error('Error saving item')
        }
        setSavingSlipItem(false)
    }

    async function handleDeleteSlipItem(type, itemUuid) {
        if (isReadOnly) return
        if (!confirm('Are you sure you want to delete this item?')) return

        try {
            let response
            if (type === 'packing') {
                response = await timesheetApi.deletePackingSlipItem(itemUuid)
            } else if (type === 'vendor') {
                response = await timesheetApi.deleteVendorSlipItem(itemUuid)
            } else {
                response = await timesheetApi.deletePickupSlipItem(itemUuid)
            }

            if (response.success) {
                toast.success('Item deleted')
                if (type === 'packing') {
                    loadPackingSlipItems()
                } else if (type === 'vendor') {
                    loadVendorSlipItems()
                } else {
                    loadPickupSlipItems()
                }
            } else {
                toast.error('Failed to delete item')
            }
        } catch (error) {
            toast.error('Error deleting item')
        }
    }

    // ==================== PARTS FUNCTIONS ====================

    function openPartModal(part = null) {
        if (isReadOnly) return
        setEditingPart(part)
        if (part) {
            setPartForm({
                part: part.part || part.partName || '',
                description: part.description || '',
                quantity: part.quantity || '',
                listId: part.listId || ''
            })
        } else {
            setPartForm({ part: '', description: '', quantity: '', listId: '' })
        }
        setShowPartModal(true)
    }

    async function handleSavePart() {
        if (!partForm.part) {
            toast.error('Part is required')
            return
        }
        if (!partForm.quantity) {
            toast.error('Quantity is required')
            return
        }

        if (!uuid) {
            setPendingParts(prev => [...prev, {
                id: Date.now(),
                part: partForm.part,
                description: partForm.description,
                quantity: partForm.quantity,
                listId: partForm.listId,
                pending: true
            }])
            toast.success('Part added — will be saved with timesheet')
            setShowPartModal(false)
            return
        }

        const payload = {
            partName: partForm.part,
            description: partForm.description,
            quantity: partForm.quantity,
            listId: partForm.listId
        }
        setSavingPart(true)
        try {
            let response
            if (editingPart) {
                response = await timesheetApi.updatePart(editingPart.uuid, payload)
            } else {
                response = await timesheetApi.addPart(uuid, payload)
            }
            if (response.success) {
                toast.success(editingPart ? 'Part updated' : 'Part added')
                setShowPartModal(false)
                loadParts()
            } else {
                toast.error(response.message || 'Failed to save part')
            }
        } catch (error) {
            toast.error('Error saving part')
        }
        setSavingPart(false)
    }

    async function handleDeletePart(partUuid) {
        if (isReadOnly) return
        if (!confirm('Are you sure you want to delete this part?')) return

        try {
            const response = await timesheetApi.deletePart(partUuid)
            if (response.success) {
                toast.success('Part deleted')
                loadParts()
            } else {
                toast.error('Failed to delete part')
            }
        } catch (error) {
            toast.error('Error deleting part')
        }
    }

    // ==================== FIELD VISIBILITY ====================
    const tsType = form.tsType

    const isTimesheet = tsType === '0'
    const isShopWork = tsType === '3'
    const isPackingOrPickup = tsType === '2' || tsType === '4'
    const isVendorSlip = tsType === '5'

    const showPlateNumber = isTimesheet
    const showSiteStartFinish = isTimesheet || isShopWork
    const showOdomFields = isTimesheet
    const showJobSite = !isShopWork && !isVendorSlip
    const showMachineDetails = !isPackingOrPickup && !isVendorSlip
    const showCustomerRequirement = tsType !== '2' && !isVendorSlip
    const showWorkPerformed = !isPackingOrPickup && !isVendorSlip
    const showMaterialUsed = isTimesheet || isShopWork
    const showSketchPad = isTimesheet || isShopWork
    const showPartsUsed = isTimesheet || isShopWork
    const showPackingSlipItems = tsType === '2'
    const showPickupSlipItems = tsType === '4'
    const showVendorSlipItems = isVendorSlip
    const showVendorFields = isVendorSlip
    const showSignatureField = !isShopWork

    // ==================== RENDER ====================

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner w-8 h-8"></div>
            </div>
        )
    }

    return (
        <div>
            {/* Header */}
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">
                        {isEditing ? (isReadOnly ? 'View Timesheet' : 'Edit Timesheet') : 'New Timesheet'}
                    </h1>
                    {form.referenceNo && (
                        <p className="text-blue-100 mt-1">Ref: {form.referenceNo}</p>
                    )}
                </div>
            </div>

            {/* Status Banners */}
            {isEditing && isSubmitted && (
                <div className="mb-4 p-4 rounded-lg border bg-green-50 text-green-800 border-green-300 flex items-center justify-between">
                    <div>
                        <p className="font-semibold">Submitted — Awaiting Export</p>
                        <p className="text-sm">This timesheet is pending export to QuickBooks. Return to draft to make changes.</p>
                    </div>
                    <Button variant="secondary" size="sm" onClick={handleReturnToDraft} loading={returningToDraft}>
                        Return to Draft
                    </Button>
                </div>
            )}

            {isEditing && isExported && (
                <div className="mb-4 p-4 rounded-lg border bg-blue-50 text-blue-800 border-blue-300">
                    <p className="font-semibold">Exported to QuickBooks</p>
                    <p className="text-sm">This timesheet has been exported. Use the Rollback feature in the Sync Dashboard to make changes.</p>
                </div>
            )}

            {/* Form */}
            <div className="bg-white rounded-xl shadow-md p-6">

                {/* Action buttons at top */}
                <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b">
                    {isDraft && (
                        <>
                            <Button onClick={() => handleSave()} loading={saving}>Save</Button>
                            {isEditing && (
                                <>
                                    <Button variant="success" onClick={handleSubmit} loading={submitting}>Submit</Button>
                                    <Button variant="danger" onClick={handleDelete} loading={deleting}>Delete</Button>
                                </>
                            )}
                        </>
                    )}
                    {isSubmitted && (
                        <Button variant="warning" onClick={handleReturnToDraft} loading={returningToDraft}>
                            Return to Draft
                        </Button>
                    )}
                    <Button variant="secondary" onClick={handleBack}>Back</Button>
                    {showSketchPad && !isReadOnly && (
                        <Button
                            variant="secondary"
                            onClick={async () => {
                                if (!isEditing) {
                                    if (!form.customer) {
                                        toast.error('Please fill in Customer first')
                                        return
                                    }
                                    setSaving(true)
                                    try {
                                        const response = await timesheetApi.create(form)
                                        if (response.success && response.uuid) {
                                            navigate(`/timesheet/${response.uuid}/sketches?referenceNo=${encodeURIComponent(response.timesheet?.referenceNo || '')}`, { replace: true })
                                        } else {
                                            toast.error(response.message || 'Failed to save')
                                        }
                                    } catch (error) {
                                        toast.error('Error saving timesheet')
                                    }
                                    setSaving(false)
                                } else {
                                    navigate(`/timesheet/${uuid}/sketches?referenceNo=${encodeURIComponent(form.referenceNo)}`)
                                }
                            }}
                        >
                            🎨 Sketch Pad
                        </Button>
                    )}
                </div>

                {/* Main Fields Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                    <Select
                        label="Timesheet Type"
                        value={form.tsType}
                        onChange={(e) => handleChange('tsType', e.target.value)}
                        options={TIMESHEET_TYPES}
                        disabled={isReadOnly}
                    />
                    <Input
                        label="Reference No"
                        value={form.referenceNo}
                        disabled={true}
                        placeholder="Auto-generated on save"
                    />
                    <Input
                        label="JOB #"
                        value={form.workOrderNumber}
                        onChange={(e) => handleChange('workOrderNumber', e.target.value)}
                        disabled={isReadOnly}
                        data-field="workOrderNumber"
                        error={formErrors.workOrderNumber}
                        required
                    />
                    <Input
                        label="Employee Name"
                        value={form.employeeName}
                        onChange={(e) => handleChange('employeeName', e.target.value)}
                        disabled={isReadOnly}
                        data-field="employeeName"
                        error={formErrors.employeeName}
                        required
                    />
                    {showPlateNumber && (
                        <PlateField
                            label="Plate No"
                            value={form.plateNumber}
                            onChange={(v) => handleChange('plateNumber', v)}
                            placeholder="Select plate..."
                            disabled={isReadOnly}
                            data-field="plateNumber"
                            error={formErrors.plateNumber}
                            required
                        />
                    )}
                    <Input
                        label="Customer"
                        value={form.customer}
                        onChange={(e) => handleChange('customer', e.target.value)}
                        disabled={isReadOnly}
                        data-field="customer"
                        error={formErrors.customer}
                        required
                    />
                    <Input
                        label="Contact"
                        value={form.contact}
                        onChange={(e) => handleChange('contact', e.target.value)}
                        disabled={isReadOnly}
                        data-field="contact"
                        error={formErrors.contact}
                    />
                    {showJobSite && (
                        <Input
                            label="Job Site"
                            value={form.jobSite}
                            onChange={(e) => handleChange('jobSite', e.target.value)}
                            disabled={isReadOnly}
                            data-field="jobSite"
                            error={formErrors.jobSite}
                            required
                        />
                    )}

                    {showVendorFields && (
                        <>
                            <Input
                                label="Vendor Name"
                                value={form.vendorName}
                                onChange={(e) => handleChange('vendorName', e.target.value)}
                                disabled={isReadOnly}
                                data-field="vendorName"
                                error={formErrors.vendorName}
                                required
                            />
                            <Select
                                label="Slip Type"
                                value={form.vendorSlipType}
                                onChange={(e) => handleChange('vendorSlipType', e.target.value)}
                                options={VENDOR_SLIP_TYPE_OPTIONS}
                                placeholder="Select slip type..."
                                disabled={isReadOnly}
                                data-field="vendorSlipType"
                                error={formErrors.vendorSlipType}
                                required
                            />
                        </>
                    )}

                    <Input
                        label="Date"
                        type="date"
                        value={form.month}
                        onChange={(e) => handleChange('month', e.target.value)}
                        disabled={isReadOnly}
                        data-field="month"
                        error={formErrors.month}
                        required
                    />

                    {showSiteStartFinish && (
                        <>
                            <Select
                                label="Start Time"
                                value={form.siteStart}
                                onChange={(e) => handleChange('siteStart', e.target.value)}
                                onFocus={() => handleTimeFocus('siteStart')}
                                options={TIME_OPTIONS}
                                placeholder="Select time..."
                                disabled={isReadOnly}
                                data-field="siteStart"
                                error={formErrors.siteStart}
                                required
                            />
                            <Select
                                label="Finish Time"
                                value={form.siteFinish}
                                onChange={(e) => handleChange('siteFinish', e.target.value)}
                                onFocus={() => handleTimeFocus('siteFinish')}
                                options={TIME_OPTIONS}
                                placeholder="Select time..."
                                disabled={isReadOnly}
                                data-field="siteFinish"
                                error={formErrors.siteFinish}
                                required
                            />
                        </>
                    )}

                    {showOdomFields && (
                        <>
                            <Input
                                label="Odometer Start"
                                type="number"
                                value={form.odomStart}
                                onChange={(e) => handleChange('odomStart', e.target.value)}
                                disabled={isReadOnly}
                                data-field="odomStart"
                                error={formErrors.odomStart}
                                required
                            />
                            <Input
                                label="Odometer Finish"
                                type="number"
                                value={form.odomFinish}
                                onChange={(e) => handleChange('odomFinish', e.target.value)}
                                disabled={isReadOnly}
                                data-field="odomFinish"
                                error={formErrors.odomFinish}
                                required
                            />
                        </>
                    )}

                    {showMachineDetails && (
                        <>
                            <Input
                                label="Machine Number"
                                value={form.machineNumber}
                                onChange={(e) => handleChange('machineNumber', e.target.value)}
                                disabled={isReadOnly}
                                data-field="machineNumber"
                                error={formErrors.machineNumber}
                                required
                            />
                            <Input
                                label="Make"
                                value={form.make}
                                onChange={(e) => handleChange('make', e.target.value)}
                                disabled={isReadOnly}
                                data-field="make"
                                error={formErrors.make}
                                required
                            />
                            <Input
                                label="Model"
                                value={form.model}
                                onChange={(e) => handleChange('model', e.target.value)}
                                disabled={isReadOnly}
                                data-field="model"
                                error={formErrors.model}
                                required
                            />
                            <Input
                                label="Serial No"
                                value={form.serialNo}
                                onChange={(e) => handleChange('serialNo', e.target.value)}
                                disabled={isReadOnly}
                                data-field="serialNo"
                                error={formErrors.serialNo}
                                required
                            />
                        </>
                    )}
                </div>

                {showCustomerRequirement && (
                    <div className="mt-6" data-field="customerRequirement">
                        <TextArea
                            label="Customer Requirement"
                            value={form.customerRequirement}
                            onChange={(e) => handleChange('customerRequirement', e.target.value)}
                            rows={3}
                            maxLength={2400}
                            showCount
                            disabled={isReadOnly}
                            error={formErrors.customerRequirement}
                            required
                        />
                    </div>
                )}

                {showWorkPerformed && (
                    <div className="mt-4" data-field="workPerformed">
                        <TextArea
                            label="Work Performed (used on invoice)"
                            value={form.workPerformed}
                            onChange={(e) => handleChange('workPerformed', e.target.value)}
                            rows={6}
                            maxLength={2400}
                            showCount
                            disabled={isReadOnly}
                            error={formErrors.workPerformed}
                            required
                        />
                    </div>
                )}

                {/* Radio Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div className="form-group" data-field="jobComplete">
                        <label className="form-label">
                            Job Complete <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-4 mt-2">
                            {JOB_COMPLETE_OPTIONS.map(opt => (
                                <label key={opt.value} className={`flex items-center gap-2 ${isReadOnly ? 'cursor-default' : 'cursor-pointer'}`}>
                                    <input
                                        type="radio"
                                        name="jobComplete"
                                        value={opt.value}
                                        checked={form.jobComplete === opt.value}
                                        onChange={(e) => handleChange('jobComplete', e.target.value)}
                                        className="w-4 h-4 text-rdi-blue"
                                        disabled={isReadOnly}
                                    />
                                    <span>{opt.label}</span>
                                </label>
                            ))}
                        </div>
                        {formErrors.jobComplete && (
                            <p className="text-red-500 text-xs mt-1">⚠ {formErrors.jobComplete}</p>
                        )}
                    </div>

                    {showMaterialUsed && (
                        <div className="form-group" data-field="materialUsed">
                            <label className="form-label">
                                Material Used <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-4 mt-2">
                                {MATERIAL_USED_OPTIONS.map(opt => (
                                    <label key={opt.value} className={`flex items-center gap-2 ${isReadOnly ? 'cursor-default' : 'cursor-pointer'}`}>
                                        <input
                                            type="radio"
                                            name="materialUsed"
                                            value={opt.value}
                                            checked={form.materialUsed === opt.value}
                                            onChange={(e) => handleChange('materialUsed', e.target.value)}
                                            className="w-4 h-4 text-rdi-blue"
                                            disabled={isReadOnly}
                                        />
                                        <span>{opt.label}</span>
                                    </label>
                                ))}
                            </div>
                            {formErrors.materialUsed && (
                                <p className="text-red-500 text-xs mt-1">⚠ {formErrors.materialUsed}</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Parts Used Section */}
                {showPartsUsed && (
                    <div className="mt-6 pt-4 border-t">
                        <div className="flex items-center justify-between mb-4">
                            <label className="form-label text-lg">Parts Used</label>
                            {!isReadOnly && (
                                <Button size="sm" onClick={() => openPartModal()}>+ Add Part</Button>
                            )}
                        </div>
                        {parts.length === 0 && pendingParts.length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-lg text-gray-500"><p>No parts added yet</p></div>
                        ) : (
                            <div className="space-y-2">
                                {parts.map((part) => (
                                    <div key={part.uuid} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                                        <div className={`flex-1 ${!isReadOnly ? 'cursor-pointer' : ''}`} onClick={() => !isReadOnly && openPartModal(part)}>
                                            <p className="font-medium">{part.part || part.partName} (Qty: {part.quantity})</p>
                                            <p className="text-sm text-gray-500">{part.description || 'No description'}</p>
                                        </div>
                                        {!isReadOnly && (
                                            <button onClick={() => handleDeletePart(part.uuid)} className="text-red-500 hover:text-red-700 p-2">🗑️</button>
                                        )}
                                    </div>
                                ))}
                                {pendingParts.map((part) => (
                                    <div key={part.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                                        <div className="flex-1">
                                            <p className="font-medium">{part.part} (Qty: {part.quantity}) <span className="text-xs text-yellow-600 ml-1">• unsaved</span></p>
                                            <p className="text-sm text-gray-500">{part.description || 'No description'}</p>
                                        </div>
                                        <button onClick={() => setPendingParts(prev => prev.filter(p => p.id !== part.id))} className="text-red-500 hover:text-red-700 p-2">🗑️</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Packing Slip Items */}
                {showPackingSlipItems && (
                    <div className="mt-6 pt-4 border-t">
                        <div className="flex items-center justify-between mb-4">
                            <label className="form-label text-lg">Packing Slip Items</label>
                            {!isReadOnly && <Button size="sm" onClick={() => openSlipItemModal('packing')}>+ Add Item</Button>}
                        </div>
                        {packingSlipItems.length === 0 && pendingSlipItems.filter(i => i.type === 'packing').length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-lg text-gray-500"><p>No items added yet</p></div>
                        ) : (
                            <div className="space-y-2">
                                {packingSlipItems.map((item) => (
                                    <div key={item.uuid} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                                        <div className={`flex-1 ${!isReadOnly ? 'cursor-pointer' : ''}`} onClick={() => !isReadOnly && openSlipItemModal('packing', item)}>
                                            <p className="font-medium">Qty: {item.quantity} | Make: {item.make || '-'} | Model: {item.model || '-'}</p>
                                            <p className="text-sm text-gray-500">Serial: {item.serialNumber || '-'}</p>
                                            {item.notes && <p className="text-sm text-blue-600 mt-1">Notes: {item.notes}</p>}
                                        </div>
                                        {!isReadOnly && <button onClick={() => handleDeleteSlipItem('packing', item.uuid)} className="text-red-500 hover:text-red-700 p-2">🗑️</button>}
                                    </div>
                                ))}
                                {pendingSlipItems.filter(i => i.type === 'packing').map((item) => (
                                    <div key={item.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                                        <div className="flex-1">
                                            <p className="font-medium">Qty: {item.quantity} | Make: {item.make || '-'} | Model: {item.model || '-'} <span className="text-xs text-yellow-600 ml-1">• unsaved</span></p>
                                            <p className="text-sm text-gray-500">Serial: {item.serialNumber || '-'}</p>
                                            {item.notes && <p className="text-sm text-blue-600 mt-1">Notes: {item.notes}</p>}
                                        </div>
                                        <button onClick={() => setPendingSlipItems(prev => prev.filter(p => p.id !== item.id))} className="text-red-500 hover:text-red-700 p-2">🗑️</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Pickup Slip Items */}
                {showPickupSlipItems && (
                    <div className="mt-6 pt-4 border-t">
                        <div className="flex items-center justify-between mb-4">
                            <label className="form-label text-lg">Pickup Slip Items</label>
                            {!isReadOnly && <Button size="sm" onClick={() => openSlipItemModal('pickup')}>+ Add Item</Button>}
                        </div>
                        {pickupSlipItems.length === 0 && pendingSlipItems.filter(i => i.type === 'pickup').length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-lg text-gray-500"><p>No items added yet</p></div>
                        ) : (
                            <div className="space-y-2">
                                {pickupSlipItems.map((item) => (
                                    <div key={item.uuid} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                                        <div className={`flex-1 ${!isReadOnly ? 'cursor-pointer' : ''}`} onClick={() => !isReadOnly && openSlipItemModal('pickup', item)}>
                                            <p className="font-medium">Qty: {item.quantity} | Make: {item.make || '-'} | Model: {item.model || '-'}</p>
                                            <p className="text-sm text-gray-500">Serial: {item.serialNumber || '-'}</p>
                                            {item.notes && <p className="text-sm text-blue-600 mt-1">Notes: {item.notes}</p>}
                                        </div>
                                        {!isReadOnly && <button onClick={() => handleDeleteSlipItem('pickup', item.uuid)} className="text-red-500 hover:text-red-700 p-2">🗑️</button>}
                                    </div>
                                ))}
                                {pendingSlipItems.filter(i => i.type === 'pickup').map((item) => (
                                    <div key={item.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                                        <div className="flex-1">
                                            <p className="font-medium">Qty: {item.quantity} | Make: {item.make || '-'} | Model: {item.model || '-'} <span className="text-xs text-yellow-600 ml-1">• unsaved</span></p>
                                            <p className="text-sm text-gray-500">Serial: {item.serialNumber || '-'}</p>
                                            {item.notes && <p className="text-sm text-blue-600 mt-1">Notes: {item.notes}</p>}
                                        </div>
                                        <button onClick={() => setPendingSlipItems(prev => prev.filter(p => p.id !== item.id))} className="text-red-500 hover:text-red-700 p-2">🗑️</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Vendor Slip Items */}
                {showVendorSlipItems && (
                    <div className="mt-6 pt-4 border-t">
                        <div className="flex items-center justify-between mb-4">
                            <label className="form-label text-lg">Vendor Slip Items</label>
                            {!isReadOnly && <Button size="sm" onClick={() => openSlipItemModal('vendor')}>+ Add Item</Button>}
                        </div>
                        {vendorSlipItems.length === 0 && pendingSlipItems.filter(i => i.type === 'vendor').length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-lg text-gray-500"><p>No items added yet</p></div>
                        ) : (
                            <div className="space-y-2">
                                {vendorSlipItems.map((item) => (
                                    <div key={item.uuid} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                                        <div className={`flex-1 ${!isReadOnly ? 'cursor-pointer' : ''}`} onClick={() => !isReadOnly && openSlipItemModal('vendor', item)}>
                                            <p className="font-medium">Qty: {item.quantity} | Make: {item.make || '-'} | Model: {item.model || '-'}</p>
                                            <p className="text-sm text-gray-500">Serial: {item.serialNumber || '-'}</p>
                                            {item.notes && <p className="text-sm text-blue-600 mt-1">Notes: {item.notes}</p>}
                                        </div>
                                        {!isReadOnly && <button onClick={() => handleDeleteSlipItem('vendor', item.uuid)} className="text-red-500 hover:text-red-700 p-2">🗑️</button>}
                                    </div>
                                ))}
                                {pendingSlipItems.filter(i => i.type === 'vendor').map((item) => (
                                    <div key={item.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                                        <div className="flex-1">
                                            <p className="font-medium">Qty: {item.quantity} | Make: {item.make || '-'} | Model: {item.model || '-'} <span className="text-xs text-yellow-600 ml-1">• unsaved</span></p>
                                            <p className="text-sm text-gray-500">Serial: {item.serialNumber || '-'}</p>
                                            {item.notes && <p className="text-sm text-blue-600 mt-1">Notes: {item.notes}</p>}
                                        </div>
                                        <button onClick={() => setPendingSlipItems(prev => prev.filter(p => p.id !== item.id))} className="text-red-500 hover:text-red-700 p-2">🗑️</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Signature Section */}
                {showSignatureField && (
                    <div className="mt-6 pt-4 border-t">
                        <label className="form-label">Signature</label>
                        <div className="mt-2">
                            {form.signature ? (
                                <div className="border rounded-lg p-4 bg-gray-50">
                                    <img src={form.signature} alt="Signature" className="max-h-24 mx-auto" />
                                    {!isReadOnly && (
                                        <Button variant="secondary" size="sm" className="mt-2" onClick={() => setShowSignature(true)}>Update Signature</Button>
                                    )}
                                </div>
                            ) : (
                                !isReadOnly ? (
                                    <Button variant="outline" block onClick={() => setShowSignature(true)}>Add Signature</Button>
                                ) : (
                                    <div className="text-center py-4 bg-gray-50 rounded-lg text-gray-500"><p>No signature</p></div>
                                )
                            )}
                        </div>
                    </div>
                )}

                {/* Bottom Save/Submit Buttons */}
                {isDraft && (
                    <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t">
                        <Button onClick={() => handleSave()} loading={saving}>Save</Button>
                        {isEditing && (
                            <Button variant="success" onClick={handleSubmit} loading={submitting}>Submit</Button>
                        )}
                    </div>
                )}
            </div>

            {/* Signature Modal */}
            <Modal isOpen={showSignature} onClose={() => setShowSignature(false)} title="Customer/Vendor Signature" size="lg">
                <SignaturePad onSave={handleSignatureSave} onCancel={() => setShowSignature(false)} />
            </Modal>

            {/* Slip Item Modal */}
            <Modal
                isOpen={showSlipItemModal}
                onClose={() => setShowSlipItemModal(false)}
                title={`${editingSlipItem ? 'Edit' : 'Add'} ${slipItemType === 'packing' ? 'Packing' : slipItemType === 'vendor' ? 'Vendor' : 'Pickup'} Slip Item`}
                size="md"
            >
                <div className="space-y-4">
                    <Input label="Make" value={slipItemForm.make} onChange={(e) => setSlipItemForm(prev => ({ ...prev, make: e.target.value }))} />
                    <Input label="Model" value={slipItemForm.model} onChange={(e) => setSlipItemForm(prev => ({ ...prev, model: e.target.value }))} />
                    <Input label="Serial Number" value={slipItemForm.serialNumber} onChange={(e) => setSlipItemForm(prev => ({ ...prev, serialNumber: e.target.value }))} />
                    <Input label="Quantity" value={slipItemForm.quantity} onChange={(e) => setSlipItemForm(prev => ({ ...prev, quantity: e.target.value }))} required />
                    <TextArea
                        label="Notes"
                        value={slipItemForm.notes}
                        onChange={(e) => setSlipItemForm(prev => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                        maxLength={500}
                        showCount
                    />
                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                        <Button variant="secondary" onClick={() => setShowSlipItemModal(false)}>Cancel</Button>
                        <Button onClick={handleSaveSlipItem} loading={savingSlipItem}>{editingSlipItem ? 'Update' : 'Add'} Item</Button>
                    </div>
                </div>
            </Modal>

            {/* Parts Modal */}
            <Modal isOpen={showPartModal} onClose={() => setShowPartModal(false)} title={`${editingPart ? 'Edit' : 'Add'} Part`} size="md">
                <div className="space-y-4">
                    <Input
                        label="Part"
                        value={partForm.part}
                        onChange={(e) => setPartForm(prev => ({ ...prev, part: e.target.value }))}
                        placeholder="Enter part name"
                        required
                    />
                    <Input label="Description" value={partForm.description} onChange={(e) => setPartForm(prev => ({ ...prev, description: e.target.value }))} />
                    <Input label="Quantity" value={partForm.quantity} onChange={(e) => setPartForm(prev => ({ ...prev, quantity: e.target.value }))} required />
                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                        <Button variant="secondary" onClick={() => setShowPartModal(false)}>Cancel</Button>
                        <Button onClick={handleSavePart} loading={savingPart}>{editingPart ? 'Update' : 'Add'} Part</Button>
                    </div>
                </div>
            </Modal>
        </div>
    )
}